import { useEffect, useMemo, useRef, useState } from 'react';

import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { KimInput } from './kim-input';
import { useKimFileUpload } from '../hooks/use-kim-file-upload';
import { TEMPLATES } from '../templates';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateKimThread } from '@/api/mutations/useCreateKimThread';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { BetaBadge } from '@/new/components/beta-badge';
import { DocMentionsInputHandle } from '@/new/components/doc-mentions-input';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

export function NewKimLargeInput() {
  const { matterId, subsetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [question, setQuestion] = useState('');
  const inputRef = useRef<DocMentionsInputHandle>(null);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const questionFromSearch = next.get('question')?.trim();

    if (questionFromSearch) {
      next.delete('question');
      setSearchParams(next, { replace: true });
      setQuestion(questionFromSearch);
      return;
    }

    const templateId = next.get('template');
    if (templateId) {
      const template = TEMPLATES.find((t) => t.id === templateId);
      if (template) {
        setQuestion(template.template);
      }

      next.delete('template');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.has('question') || searchParams.has('template')) return;

    const questionFromState = (location.state as { question?: string } | null)?.question?.trim();
    if (questionFromState) {
      setQuestion(questionFromState);
    }
  }, []);

  const { mutate: createThread, isLoading } = useCreateKimThread(matterId, subsetId);
  const { uploadedFiles, isUploading, addFiles, removeFile, clearFiles } = useKimFileUpload(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);

  const suggestedQuestions = useMemo(() => {
    const questions = subset?.exampleKimQuestions?.question?.map((q) => q.prompt) ?? [];
    const drafts = subset?.exampleKimQuestions?.draft?.map((q) => q.prompt) ?? [];
    return [...questions, ...drafts];
  }, [subset]);

  const handleSubmit = () => {
    const trimmed = question.trim();
    if (!trimmed || isUploading) return;

    const files = uploadedFiles.length > 0 ? uploadedFiles : undefined;

    createThread(
      { message: trimmed, files },
      {
        onSuccess: (data) => {
          clearFiles();
          navigate(`/matters/${matterId}/subsets/${subsetId}/new-kim/${data.id}`);
        },
        onError: () => {
          toast.error('Error creating thread');
        },
      },
    );
  };

  return (
    <div className="mb-12 flex w-full max-w-xl flex-col items-center">
      <Tooltip>
        <TooltipTrigger>
          <BetaBadge className="mb-3" />
        </TooltipTrigger>
        <TooltipContent sideOffset={14}>
          This version of Kim is still undergoing testing. Please send any feedback to{' '}
          <a href="mailto:support@wexler.ai" className="text-blue-500">
            support@wexler.ai
          </a>
          .
        </TooltipContent>
      </Tooltip>

      <h2 className="mb-8 font-serif text-2xl font-medium">Ask Kim...</h2>

      <div className="mb-1.5 flex w-full justify-between">
        <div className="flex gap-1">
          <Button variant={'ghost'} size={'sm'} onClick={() => inputRef.current?.triggerMention()}>
            <Icon className="lucide--at-sign" /> Mention documents
          </Button>
        </div>

        <div className="flex">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant={'ghost'} size={'sm'} className="pr-0">
                Suggested Questions
                <Icon className="lucide--chevron-down" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="group/suggestions -mt-1.5 max-h-90 max-w-md space-y-1.5 bg-gray-100 p-1.5"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              {suggestedQuestions.length === 0 ? (
                <DropdownMenuItem disabled className="text-xs">
                  No suggested questions
                </DropdownMenuItem>
              ) : (
                <>
                  <p className="px-1.5 py-px text-xs text-gray-400">
                    <strong>{suggestedQuestions.length}</strong> suggested question
                    {suggestedQuestions.length === 1 ? '' : 's'}
                  </p>
                  {suggestedQuestions.map((prompt, index) => (
                    <DropdownMenuItem
                      key={index}
                      className="bg-white px-2.25 py-1.5 whitespace-normal text-gray-700 hover:bg-white! group-has-[:hover]/suggestions:[&:not(:hover)]:text-gray-600 group-has-[:hover]/suggestions:[&:not(:hover)]:opacity-80"
                      onClick={() => {
                        setQuestion(prompt);
                        inputRef.current?.focus();
                      }}
                    >
                      {prompt}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant={'ghost'} size={'sm'}>
                Templates
                <Icon className="lucide--chevron-down" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="-mt-1.5 mr-1" onCloseAutoFocus={(e) => e.preventDefault()}>
              {TEMPLATES.map((template) => (
                <DropdownMenuItem
                  key={template.id}
                  onClick={() => {
                    setQuestion(template.template);
                    inputRef.current?.focus();
                  }}
                  className="text-[13px]"
                >
                  <Icon className={template.icon} /> {template.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <KimInput
        ref={inputRef}
        className="min-h-28 w-full p-4 py-3 pb-0"
        placeholder="What is this Matter about?"
        value={question}
        onChange={setQuestion}
        onSubmit={handleSubmit}
        disabled={isLoading}
        uploadedFiles={uploadedFiles}
        isUploading={isUploading}
        onFilesAdded={addFiles}
        onFileRemove={removeFile}
        autoFocus
      />

      <Link
        to={`/matters/${matterId}/subsets/${subsetId}/kim`}
        className="mt-5 flex items-center justify-center gap-1.5 text-center text-xs text-gray-400 duration-150 hover:text-gray-600"
        onClick={() => {
          trackCustomEvent(TrackingEventsV2.KimReturnToOldExperience, { matterId, subsetId });
          localStorage.setItem('isUsingNewKim', 'false');
        }}
      >
        <Icon className="lucide--undo-2 mb-0.5 -ml-1" /> Return to the old Kim experience
      </Link>
    </div>
  );
}
